
























































import axios from 'axios';
import Component from 'vue-class-component';

import { BaseVue } from '@/BaseVue';
import UiButton from '@/components/ui/UiButton.vue';
import UiDatePicker from '@/components/ui/UiDatePicker.vue';
import UiLoading from '@/components/ui/UiLoading.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import UiToggle from '@/components/ui/UiToggle.vue';
import Beta from '@/components/util/Beta.vue';
import { InactivityWatcher } from '@/inactivityWatcher';
import { MUT_SNACKBAR } from '@/store';
import { extractBlobErrorMessage, triggerBlobDownload } from '@/utils/blobDownload';
import { getClusterAwareBaseUrl } from '@/utils/clusterAwareUrl';
import { clearDownloadIframes, downloadAuthorizedFile } from '@/utils/downloadFile';
import { downloadExportFiles } from '@/utils/exportResponseHandler';
import { requestParentToKeepActive } from '@/utils/iframeMessageRequester';

import { baConfig } from '../../../../config';

const V3_EXPORT_POLL_INTERVAL_MS = 2000;
const V3_EXPORT_POLL_TIMEOUT_MS = 30 * 60 * 1000;

@Component({
  components: {
    UiButton,
    UiDatePicker,
    UiToggle,
    UiLoading,
    Beta,
    UiPageHeader,
  },
})
export default class Export extends BaseVue {
  public fromDate = '';
  public toDate = '';
  public isLoading = false;
  public exportMode = 'Transaction Lines';
  public isNew = true;
  public isDeletedIgnored = false;
  public useOrgTimezone = true;
  public exportStatusMessage = '';

  public baseUrl = getClusterAwareBaseUrl(process.env.VUE_APP_RPT_API_URL ?? process.env.VUE_APP_API_URL!);

  // V3 report-runs live on the main API host, not the report host.
  get apiBaseUrl(): string {
    const url = baConfig.apiUrl ?? '';
    return url.endsWith('/') ? url : `${url}/`;
  }

  get useV3AsyncExport(): boolean {
    return !this.isDeletedIgnored && this.checkFeatureFlag('v3-transactions-export', this.features);
  }

  mounted() {
    const queryParams = this.$route.query;
    console.log('Export2 Query params:', queryParams);
    if (queryParams.datawarehouse && typeof queryParams.datawarehouse === 'string') {
      this.isNew = queryParams.datawarehouse === 'true';
    }
    if (queryParams.deletedIgnored && typeof queryParams.deletedIgnored === 'string') {
      this.isDeletedIgnored = queryParams.deletedIgnored === 'true';
      console.log('Export2 Set isDeletedIgnored to:', this.isDeletedIgnored);
    }
  }

  isValidDate(dateString: string) {
    // Parse the date string into a Date object
    var dateObj = new Date(dateString);

    // Check if the date object is valid and the string matches the expected format
    return !isNaN(dateObj.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  }

  get isValid() {
    return this.isValidDate(this.fromDate) && this.isValidDate(this.toDate);
  }

  private buildExportUrl(): string {
    const orgId = this.$store.state.currentOrg.id;
    const baseEndpoint = this.isDeletedIgnored
      ? `v2/orgs/${orgId}/deleted-ignored-transactions-export`
      : `v2/export/transactions`;

    const params = [];

    // Regular transactions need orgId as query param
    if (!this.isDeletedIgnored) {
      params.push(`orgId=${orgId}`);
    }

    if (this.fromDate) {
      params.push(`startDate=${this.fromDate}`);
    }

    if (this.toDate) {
      params.push(`endDate=${this.toDate}`);
    }

    if (this.useOrgTimezone) {
      params.push(`useOrgTimezone=${this.useOrgTimezone}`);
    }

    return `${this.baseUrl}${baseEndpoint}${params.length > 0 ? '?' + params.join('&') : ''}`;
  }

  public async downloadCSV() {
    this.isLoading = true;

    requestParentToKeepActive('report', true);
    const inactivityWatcherKeepActive = InactivityWatcher.instance?.keepActive(
      () => this.isLoading && document.contains(this.$el)
    );
    try {
      if (this.useV3AsyncExport) {
        await this.runV3Export();
      } else {
        await this.runLegacyExport();
      }
    } catch (err: any) {
      this.showErrorMessage(err?.message);
    } finally {
      this.isLoading = false;
      this.exportStatusMessage = '';
      requestParentToKeepActive('report', false);
      inactivityWatcherKeepActive?.dispose();
    }
  }

  /** api-svc error bodies vary: {error}, {message}, or a plain-text string (tsoa ValidationException). */
  private serverMessageFrom(data: any): string | undefined {
    if (typeof data === 'string' && data.length > 0 && data.length <= 300 && !data.startsWith('<')) {
      return data;
    }
    return data?.error || data?.message || undefined;
  }

  private async runLegacyExport() {
    const exportUrl = this.buildExportUrl();
    console.log('Export2 downloadCSV - URL:', exportUrl);

    const resp = await axios({ method: 'post', url: exportUrl, withCredentials: true });
    if (resp.status !== 200) {
      throw new Error(this.serverMessageFrom(resp.data) || undefined);
    }
    clearDownloadIframes();
    await downloadExportFiles(resp.data, this.baseUrl, this.$store.state.currentOrg.id, this.downloadFile);
  }

  /**
   * Async export via the V3 reporting API ('transactions-export-report').
   * The report runs server-side (Temporal), so the request can't die on the
   * load balancer's 10-minute timeout the way the synchronous export does for
   * full-history date ranges. V3 always uses the org timezone.
   */
  private async runV3Export() {
    const orgId = this.$store.state.currentOrg.id;
    const base = this.apiBaseUrl;

    this.exportStatusMessage = 'Starting export…';
    const startResp = await axios.post(
      `${base}v2/orgs/${orgId}/report-runs`,
      {
        reportType: 'transactions-export-report',
        inputs: [
          { key: 'startDate', value: this.fromDate },
          { key: 'endDate', value: this.toDate },
        ],
      },
      { withCredentials: true }
    );
    // Global axios resolves non-2xx (validateStatus: () => true) — check explicitly.
    if (startResp.status >= 400 || !startResp.data?.reportRunId) {
      throw new Error(this.serverMessageFrom(startResp.data) || `Failed to start export (HTTP ${startResp.status})`);
    }
    const reportRunId = startResp.data.reportRunId;

    this.exportStatusMessage = 'Generating export… this can take a few minutes for large date ranges.';
    const deadline = Date.now() + V3_EXPORT_POLL_TIMEOUT_MS;
    for (;;) {
      // The run continues server-side; stop polling if the user has left the page.
      if (!document.contains(this.$el)) {
        return;
      }
      const statusResp = await axios.get(`${base}v2/orgs/${orgId}/report-runs/${reportRunId}/status`, {
        withCredentials: true,
      });
      if (statusResp.status >= 400) {
        throw new Error(`Failed to check export status (HTTP ${statusResp.status})`);
      }
      const status = statusResp.data?.status;
      if (status === 'succeeded') {
        break;
      }
      if (status === 'failed' || status === 'timed-out') {
        throw new Error(`Export ${status}. Try a smaller date range or contact support.`);
      }
      if (Date.now() > deadline) {
        throw new Error('Timed out waiting for the export to complete. It may still finish server-side.');
      }
      await new Promise((resolve) => setTimeout(resolve, V3_EXPORT_POLL_INTERVAL_MS));
    }

    this.exportStatusMessage = 'Downloading…';
    // The V3 download endpoint streams the CSV with an Authorization header —
    // no signed URL — so it has to come down as a blob.
    const dlResp = await axios.get(`${base}v2/orgs/${orgId}/report-runs/${reportRunId}/download`, {
      withCredentials: true,
      responseType: 'blob',
    });
    if (dlResp.status >= 400) {
      throw new Error(await extractBlobErrorMessage(dlResp.status, dlResp.data));
    }
    triggerBlobDownload(dlResp.data, `transactions-export-${reportRunId}.csv`);
    this.showSuccessMessage();
  }

  downloadFile = downloadAuthorizedFile;

  showErrorMessage(message?: string) {
    this.$store.commit(MUT_SNACKBAR, {
      color: 'error',
      message: message || 'Failed to export. Try again later',
    });
  }

  showSuccessMessage() {
    this.$store.commit(MUT_SNACKBAR, {
      color: 'success',
      message: 'File download completed',
    });
  }

  toggleUi(val: boolean) {
    this.$emit('toggleUi', val);
    localStorage.setItem('exportsUi', val ? 'new' : 'old');
  }
}
